<template>
    <div>
        <dashboard-filters
            :has_active_filters="has_active_filters"
            :filter_by_name="filter_by_name"
            :filter_by_address="filter_by_address"
            :filter_by_closing_month="filter_by_closing_month"
            @filterByName="filterByName"
            @filterByAddress="filterByAddress"
            @filterByClosingMonth="filterByClosingMonth"
            @clearFilters="clearFilters"
        />

        <div class="flex items-center justify-between border-b border-gray-400 pb-4 mt-4 mb-4">
            <div class="text-sm sm:text-base flex-none">
                {{ filtered_properties.length }} {{ filtered_properties.length == 1 ? $t('Property') : $t('Properties') }}
            </div>

            <div v-if="filtered_properties.length > 1"
                class="pl-6 text-right uppercase text-gray-500 font-semibold text-xs tracking-widest select-none"
            >
                {{ $t('Sort by Closing Date:') }}

                <span v-if="sort_direction === 'desc'"
                    class="text-blue-500 hover:text-blue-700 cursor-pointer"
                    @click="sortBy('asc')"
                >
                    {{ $t('Descending') }}

                    <i class="text-sm fas fa-caret-down"></i>
                </span>

                <span v-else
                    class="text-blue-500 hover:text-blue-700 cursor-pointer"
                    @click="sortBy('desc')"
                >
                    {{ $t('Ascending') }}

                    <i class="text-sm fas fa-caret-up"></i>
                </span>
            </div>
        </div>

        <div v-if="sorted_active_properties.length">
            <h2 class="text-lg text-semibold text-blue-600 pt-2 pb-6">
                {{ $t('Active') }}
            </h2>

            <dashboard-property v-for="property in sorted_active_properties"
                :key="property.id"
                :property="property"
                size="small"
                :route="route"
                :detail_route="detail_route + property.id"
                :show_steps="true"
                :archivable="true"
                :confirm_archive="false"
                :deletable="false"
                @archived="archived(property)"
            ></dashboard-property>
        </div>

        <div v-if="sorted_archived_properties.length">
            <h2 class="text-lg text-semibold text-red-500 border-t border-gray-400 pt-6 pb-6">
                {{ $t('Archived') }}
            </h2>

            <dashboard-property v-for="property in sorted_archived_properties"
                :key="property.id"
                :property="property"
                size="small"
                :route="route"
                :detail_route="detail_route + property.id"
                :show_steps="false"
                :archivable="false"
                :deletable="true"
                @restored="restored(property)"
                @deleted="deleted(property)"
            ></dashboard-property>
        </div>
    </div>
</template>

<script>
import DashboardProperty from '../../dashboard/dashboard-property.vue';
import DashboardFilters from '../../dashboard/dashboard-filters.vue';
import { orderBy } from 'lodash';

export default {
    components: {
        DashboardProperty,
        DashboardFilters,
    },

    props: {
        initial_properties: { default: () => [] },
        broker: {},
        route: {},
        detail_route: {},
    },

    data() {
        return {
            properties: this.initial_properties,
            filter_by_status: null,
            filter_by_name: null,
            filter_by_address: null,
            filter_by_closing_month: null,
            sort_direction: 'desc',
        }
    },

    computed: {
        properties_with_dates() {
            return this.properties.map(property => {
                let closing_date = property.closing.scheduled_closing_date;

                property.closing_date_text = closing_date
                    ? dayjs(closing_date).format('MMMM D, YYYY')
                    : '';
                property.closing_month_number = closing_date
                    ? dayjs(closing_date).format('M')
                    : null;
                property.closing_date = closing_date
                    ? dayjs(closing_date).format('YYYY-MM-DD')
                    : null;

                return property;
            });
        },
        sorted_properties() {
            return orderBy(
                this.filtered_properties,
                [
                    property => property.closing_date || (
                        this.sort_direction == 'asc'
                            ? '9999-99-99'
                            : '0000-00-00'
                    ),
                    property => property.client.name.toLowerCase()
                ],
                [
                    this.sort_direction,
                    'asc'
                ]
            );
        },
        sorted_active_properties() {
            return this.sorted_properties.filter(property => ! property.archived_at);
        },
        sorted_archived_properties () {
            return this.sorted_properties.filter(property => property.archived_at);
        },
        filtered_properties() {
            let properties = this.properties_with_dates.filter(property => {
                return ! property.deleted_at;
            });

            if (this.filter_by_status) {
                properties = properties.filter(property => {
                    return property.status.overall === this.filter_by_status;
                });
            }

            if (this.filter_by_name) {
                properties = properties.filter(property => {
                    if (! property.client.name) {
                        return;
                    }

                    return property.client.name
                        .toLowerCase()
                        .includes(
                            this.filter_by_name.toLowerCase()
                        );
                })
            }

            if (this.filter_by_address) {
                properties = properties.filter(property => {
                    if (! property.address) {
                        return;
                    }

                    return (property.address + ' ' + property.city + ' ' + property.state)
                        .toLowerCase()
                        .includes(
                            this.filter_by_address.toLowerCase()
                        );
                })
            }

            if (this.filter_by_closing_month) {
                properties = properties.filter(property => {
                    if (! property.closing_date) {
                        return;
                    }

                    if (parseInt(this.filter_by_closing_month)) {
                        return property.closing_month_number
                            == this.filter_by_closing_month;
                    } else {
                        return property.closing_date_text
                            .toLowerCase()
                            .startsWith(
                                this.filter_by_closing_month.toLowerCase()
                            );
                    }
                })
            }

            return properties;
        },
        has_active_filters() {
            return this.filter_by_status
                || this.filter_by_name
                || this.filter_by_address
                || this.filter_by_closing_month;
        },
    },

    methods: {
        filterByStatus(val) {
            this.filter_by_status = val;
        },
        filterByName(val) {
            this.filter_by_name = val;
        },
        filterByAddress(val) {
            this.filter_by_address = val;
        },
        filterByClosingMonth(val) {
            this.filter_by_closing_month = val;
        },
        clearFilters() {
            this.filter_by_status = null;
            this.filter_by_name = null;
            this.filter_by_address = null;
            this.filter_by_closing_month = null;
        },
        sortBy(direction) {
            this.sort_direction = direction;
        },
        restored(property) {
            property.archived_at = null;
        },
        archived(property) {
            property.archived_at = 'now';
        },
        deleted(property) {
            property.deleted_at = 'now';
        },
    },
}
</script>
